import React, { useState } from "react";
import "./HealthOperation.scss";

// ── static data ───────────────────────────────────────────────────────────────
const services = [
  {
    id: 1,
    name: "API Backend",
    status: "healthy",
    lastCheck: "11:35",
    detail: "Response 42 ms",
    uptime: "99.98%",
  },
  {
    id: 2,
    name: "Matrix / Synapse",
    status: "healthy",
    lastCheck: "11:35",
    detail: "Rooms sync OK",
    uptime: "99.95%",
  },
  {
    id: 3,
    name: "PostgreSQL App DB",
    status: "healthy",
    lastCheck: "11:35",
    detail: "Connections 18/100",
    uptime: "99.99%",
  },
  {
    id: 4,
    name: "Object Storage",
    status: "healthy",
    lastCheck: "11:34",
    detail: "Bucket reachable",
    uptime: "100%",
  },
  {
    id: 5,
    name: "WhatsApp Webhook",
    status: "warning",
    lastCheck: "11:32",
    detail: "1 failed media download",
    uptime: "98.20%",
  },
  {
    id: 6,
    name: "Notification Worker",
    status: "healthy",
    lastCheck: "11:35",
    detail: "Queue depth 12",
    uptime: "99.90%",
  },
  {
    id: 7,
    name: "Media Processing",
    status: "healthy",
    lastCheck: "11:33",
    detail: "Queue depth 4",
    uptime: "99.80%",
  },
  {
    id: 8,
    name: "Backup Service",
    status: "healthy",
    lastCheck: "11:00",
    detail: "Last backup 03:00",
    uptime: "99.70%",
  },
];

const queues = [
  {
    id: 1,
    name: "WhatsApp Inbound",
    depth: 3,
    failed: 1,
    processed: 1420,
    status: "warning",
  },
  {
    id: 2,
    name: "Media Downloads",
    depth: 0,
    failed: 0,
    processed: 892,
    status: "healthy",
  },
  {
    id: 3,
    name: "Push Notifications",
    depth: 12,
    failed: 0,
    processed: 24531,
    status: "healthy",
  },
  {
    id: 4,
    name: "Email Alerts",
    depth: 0,
    failed: 0,
    processed: 78,
    status: "healthy",
  },
];

const webhookLogs = [
  {
    id: 1,
    time: "11:35:02",
    source: "WhatsApp",
    event: "media_received",
    status: "healthy",
    detail: "Inspection_Report.pdf routed",
  },
  {
    id: 2,
    time: "11:34:48",
    source: "Matrix",
    event: "room_sync",
    status: "healthy",
    detail: "All rooms synced",
  },
  {
    id: 3,
    time: "11:32:17",
    source: "WhatsApp",
    event: "media_download",
    status: "warning",
    detail: "Timeout after 5 s — retrying",
  },
  {
    id: 4,
    time: "11:30:05",
    source: "FCM",
    event: "push_delivered",
    status: "healthy",
    detail: "Batch 24 delivered",
  },
  {
    id: 5,
    time: "11:28:44",
    source: "WhatsApp",
    event: "text_received",
    status: "healthy",
    detail: "Routed to Operations Command",
  },
];

const backupRows = [
  {
    id: 1,
    type: "Full DB Backup",
    lastRun: "Today 03:00",
    nextRun: "Tomorrow 03:00",
    size: "2.4 GB",
    status: "healthy",
  },
  {
    id: 2,
    type: "Media Snapshot",
    lastRun: "Today 04:00",
    nextRun: "Tomorrow 04:00",
    size: "18 GB",
    status: "healthy",
  },
  {
    id: 3,
    type: "Config Export",
    lastRun: "Yesterday 22:00",
    nextRun: "Today 22:00",
    size: "12 KB",
    status: "healthy",
  },
];

const TABS = ["Services", "Queue Monitor", "Webhook Logs", "Backup Status"];

// ── reusable mini-components ──────────────────────────────────────────────────
function StatusBadge({ status }) {
  return (
    <span className={`ho-badge ho-badge--${status}`}>
      <span className="ho-badge__dot" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function SectionTitle({ children }) {
  return <h3 className="ho-section-title">{children}</h3>;
}

// ── KPI bar ───────────────────────────────────────────────────────────────────
function KpiBar() {
  const healthy = services.filter((s) => s.status === "healthy").length;
  const warning = services.filter((s) => s.status === "warning").length;
  const error = services.filter((s) => s.status === "error").length;

  const items = [
    { label: "Total Services", value: services.length, mod: "total" },
    { label: "Healthy", value: healthy, mod: "healthy" },
    { label: "Warnings", value: warning, mod: "warning" },
    { label: "Errors", value: error, mod: "error" },
  ];

  return (
    <div className="ho-kpi-bar">
      {items.map((item) => (
        <div
          key={item.label}
          className={`ho-kpi-card ho-kpi-card--${item.mod}`}
        >
          <div className="ho-kpi-card__value">{item.value}</div>
          <div className="ho-kpi-card__label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Filter chips ──────────────────────────────────────────────────────────────
function FilterChips({ active, onChange }) {
  const chips = [
    { key: "all", label: "All" },
    { key: "healthy", label: "Healthy" },
    { key: "warning", label: "Warning" },
    { key: "error", label: "Error" },
  ];
  return (
    <div className="ho-chips">
      {chips.map((c) => (
        <button
          key={c.key}
          className={`ho-chip ${active === c.key ? "ho-chip--active" : ""}`}
          onClick={() => onChange(c.key)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  return (
    <div className="ho-tabbar">
      {TABS.map((t) => (
        <button
          key={t}
          className={`ho-tabbar__tab ${active === t ? "ho-tabbar__tab--active" : ""}`}
          onClick={() => onChange(t)}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ── Service grid ──────────────────────────────────────────────────────────────
function ServiceGrid({ filter }) {
  const filtered =
    filter === "all" ? services : services.filter((s) => s.status === filter);
  return (
    <div className="ho-service-grid">
      {filtered.map((svc) => (
        <div
          key={svc.id}
          className={`ho-service-card ho-service-card--${svc.status}`}
        >
          <div className="ho-service-card__accent" />
          <div className="ho-service-card__body">
            <div className="ho-service-card__header">
              <span className="ho-service-card__name">{svc.name}</span>
              <StatusBadge status={svc.status} />
            </div>
            <div className="ho-service-card__meta">
              <div className="ho-service-card__meta-item">
                <span className="ho-service-card__meta-label">Last Check</span>
                <span className="ho-service-card__meta-value">
                  {svc.lastCheck}
                </span>
              </div>
              <div className="ho-service-card__meta-item">
                <span className="ho-service-card__meta-label">Uptime</span>
                <span className="ho-service-card__meta-value">
                  {svc.uptime}
                </span>
              </div>
            </div>
            <div className="ho-service-card__detail">{svc.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Queue table ───────────────────────────────────────────────────────────────
function QueueTable() {
  const [retrying, setRetrying] = useState(null);
  const handleRetry = (id) => {
    setRetrying(id);
    setTimeout(() => setRetrying(null), 1500);
  };

  return (
    <div className="ho-table-wrap">
      <table className="ho-table">
        <thead>
          <tr>
            {[
              "Queue Name",
              "Depth",
              "Failed",
              "Processed Today",
              "Status",
              "Action",
            ].map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {queues.map((q) => (
            <tr key={q.id}>
              <td className="ho-table__name">{q.name}</td>
              <td
                className={`ho-table__num ${q.depth > 0 ? "ho-table__num--warn" : ""}`}
              >
                {q.depth}
              </td>
              <td
                className={`ho-table__num ${q.failed > 0 ? "ho-table__num--error" : ""}`}
              >
                {q.failed}
              </td>
              <td>{q.processed.toLocaleString()}</td>
              <td>
                <StatusBadge status={q.status} />
              </td>
              <td>
                {q.failed > 0 ? (
                  <button
                    className="ho-btn ho-btn--primary ho-btn--sm"
                    onClick={() => handleRetry(q.id)}
                  >
                    {retrying === q.id ? "Retrying…" : "⟳ Retry Failed"}
                  </button>
                ) : (
                  <span className="ho-table__empty">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Webhook log table ─────────────────────────────────────────────────────────
function WebhookTable() {
  return (
    <div className="ho-table-wrap">
      <table className="ho-table">
        <thead>
          <tr>
            {["Time", "Source", "Event", "Status", "Detail", "Action"].map(
              (h) => (
                <th key={h}>{h}</th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {webhookLogs.map((log) => (
            <tr key={log.id}>
              <td className="ho-table__mono">{log.time}</td>
              <td>
                <span className="ho-source-badge">{log.source}</span>
              </td>
              <td className="ho-table__mono">{log.event}</td>
              <td>
                <StatusBadge status={log.status} />
              </td>
              <td className="ho-table__muted">{log.detail}</td>
              <td>
                {log.status === "warning" ? (
                  <button className="ho-btn ho-btn--secondary ho-btn--sm">
                    ⟳ Retry
                  </button>
                ) : (
                  <span className="ho-table__empty">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Backup table ──────────────────────────────────────────────────────────────
function BackupTable() {
  return (
    <div className="ho-table-wrap">
      <table className="ho-table">
        <thead>
          <tr>
            {[
              "Backup Type",
              "Last Run",
              "Next Run",
              "Size",
              "Status",
              "Logs",
            ].map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {backupRows.map((b) => (
            <tr key={b.id}>
              <td className="ho-table__name">{b.type}</td>
              <td>{b.lastRun}</td>
              <td>{b.nextRun}</td>
              <td className="ho-table__muted">{b.size}</td>
              <td>
                <StatusBadge status={b.status} />
              </td>
              <td>
                <button className="ho-btn ho-btn--secondary ho-btn--sm">
                  View Logs
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function HealthOperation() {
  const [tab, setTab] = useState("Services");
  const [filter, setFilter] = useState("all");
  const [refreshed, setRefreshed] = useState(false);

  const handleRefresh = () => {
    setRefreshed(true);
    setTimeout(() => setRefreshed(false), 1200);
  };

  const hasWarning = services.some((s) => s.status === "warning");

  return (
    <div className="sec-page ho-page">
      <div className="ho-page__inner">
        {/* ── Page header ── */}
        <div className="ho-page__header">
          <div className="ho-page__header-left">
            <h1 className="ho-page__title">Health &amp; Operations</h1>
            <p className="ho-page__subtitle">
              Monitor service health, queues, jobs, backups and integration
              status.
            </p>
          </div>
          <div className="ho-page__header-actions">
            <button
              className="ho-btn ho-btn--secondary"
              onClick={handleRefresh}
            >
              {refreshed ? "✓ Refreshed" : "⟳ Refresh"}
            </button>
            <button className="ho-btn ho-btn--primary">↓ Export Report</button>
          </div>
        </div>

        {/* ── KPI bar ── */}
        <KpiBar />

        {/* ── Main tabbed card ── */}
        <div className="ho-card ho-card--tabs">
          <TabBar active={tab} onChange={setTab} />

          <div className="ho-tab-content">
            {tab === "Services" && (
              <>
                <div className="ho-tab-content__toolbar">
                  <SectionTitle>Service Health</SectionTitle>
                  <FilterChips active={filter} onChange={setFilter} />
                </div>
                <ServiceGrid filter={filter} />
              </>
            )}

            {tab === "Queue Monitor" && (
              <>
                <SectionTitle>Queue Monitor</SectionTitle>
                <QueueTable />
              </>
            )}

            {tab === "Webhook Logs" && (
              <>
                <div className="ho-tab-content__toolbar">
                  <SectionTitle>Webhook Event Log</SectionTitle>
                  <button className="ho-btn ho-btn--secondary ho-btn--sm">
                    Export CSV
                  </button>
                </div>
                <WebhookTable />
              </>
            )}

            {tab === "Backup Status" && (
              <>
                <SectionTitle>Backup Status</SectionTitle>
                <BackupTable />
              </>
            )}
          </div>
        </div>

        {/* ── Warning alert banner ── */}
        {hasWarning && (
          <div className="ho-alert ho-alert--warning">
            <span className="ho-alert__icon">⚠️</span>
            <div>
              <span className="ho-alert__label">Active Warning: </span>
              <span className="ho-alert__text">
                WhatsApp Webhook has 1 failed media download. Check the Webhook
                Logs tab for details and use Retry to attempt redelivery.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HealthOperation;
